import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';

export interface AttachPlaidAccount {
  plaid_account_id: string;
  name: string;
  official_name?: string;
  mask?: string;
  type: string;
  subtype?: string;
  currency?: string;
  ledger_type: string;
}

export interface AttachProposal {
  plaid_account: AttachPlaidAccount;
  account_id: string | null;
  confidence: '' | 'mask' | 'name';
  reason?: string;
  candidates: string[];
  linked_account_id?: string | null;
}

export interface ManualAccount {
  id: string;
  name: string;
  institution?: string;
  type: string;
  currency: string;
  last4?: string;
  transactions: number;
}

export interface AccountMapping {
  connection_id: string;
  institution: string;
  proposals: AttachProposal[];
  manual_accounts: ManualAccount[];
  mapping_required: boolean;
}

export interface MappingChoice {
  plaid_account_id: string;
  account_id: string | null;
}

const CREATE = '__create__';

/** Initial choices: the proposal where there is one, else "create new". */
export function initialChoices(m: AccountMapping): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of m.proposals) {
    if (p.linked_account_id) continue;
    out[p.plaid_account.plaid_account_id] = p.account_id ?? CREATE;
  }
  return out;
}

/** The POST body for the chosen mapping. */
export function choicesToMappings(choices: Record<string, string>): MappingChoice[] {
  return Object.entries(choices).map(([plaid_account_id, v]) => ({ plaid_account_id, account_id: v === CREATE ? null : v }));
}

/**
 * After Plaid Link: map each bank account onto an existing FolioWorth account
 * (keeping its history, valuations and rules) or create a new one (#41).
 * Identifier matches (the account number's last digits) are preselected.
 */
export const MapAccountsDialog: React.FC<{
  open: boolean;
  mapping: AccountMapping | null;
  onCancel: () => void;
  onConfirm: (mappings: MappingChoice[]) => Promise<void>;
}> = ({ open, mapping, onCancel, onConfirm }) => {
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (mapping) setChoices(initialChoices(mapping));
    setError(null);
  }, [mapping]);
  if (!mapping) return null;
  const byId = new Map(mapping.manual_accounts.map((a) => [a.id, a]));
  const used = Object.values(choices).filter((v) => v !== CREATE);
  const clash = used.length !== new Set(used).size;
  const confirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await onConfirm(choicesToMappings(choices));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Failed to link accounts');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{mapping.institution || 'Bank'} accounts → FolioWorth accounts</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You already keep some of these accounts here. Attaching keeps the account, its history, valuations and
          rules — new bank transactions you already imported are matched, not added twice.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {clash && <Alert severity="warning" sx={{ mb: 2 }}>Two bank accounts point at the same FolioWorth account.</Alert>}
        <Table size="small" aria-label="Account mapping">
          <TableHead>
            <TableRow>
              <TableCell>Bank account</TableCell>
              <TableCell>FolioWorth account</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mapping.proposals.map((p) => {
              const pa = p.plaid_account;
              const label = `${pa.name}${pa.mask ? ` ••${pa.mask}` : ''}`;
              return (
                <TableRow key={pa.plaid_account_id}>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Typography variant="body2">{label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[pa.subtype || pa.type, pa.currency].filter(Boolean).join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 320 }}>
                    {p.linked_account_id ? (
                      <Chip size="small" variant="outlined" label="Already linked" />
                    ) : (
                      <Stack spacing={0.5}>
                        <TextField select size="small" fullWidth value={choices[pa.plaid_account_id] ?? CREATE}
                          inputProps={{ 'aria-label': `Map ${label}` }}
                          onChange={(e) => setChoices((c) => ({ ...c, [pa.plaid_account_id]: e.target.value }))}>
                          <MenuItem value={CREATE}>Create a new account</MenuItem>
                          {p.candidates.map((id) => {
                            const a = byId.get(id);
                            return a ? (
                              <MenuItem key={id} value={id}>
                                Attach to {a.name}{a.transactions ? ` (${a.transactions} transactions)` : ''}
                              </MenuItem>
                            ) : null;
                          })}
                        </TextField>
                        {p.confidence && choices[pa.plaid_account_id] === p.account_id && (
                          <Typography variant="caption" color="success.main">Suggested: {p.reason ?? 'matching account number'}</Typography>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Decide later</Button>
        <Button variant="contained" onClick={() => void confirm()} disabled={saving || clash}>
          {saving ? 'Linking…' : 'Link accounts'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
